<template>
	<div class="checkout-thank-you">
		<section class="checkout-thank-you__upper">
			<div class="checkout-thank-you__header">
				<h2 class="checkout-thank-you__message">
					{{ titleWording }}
				</h2>
			</div>
			<div class="checkout-thank-you__text">
				<span class="checkout-thank-you__booking-no"> Buchungsreferenznummer: {{ shoppingCart.ShoppingCartID }} </span>
				<div class="checkout-thank-you__text-content">
					<p>
						In den nächsten 15 Minuten erhalten Sie eine E-Mail zur Bestätigung Ihres verbindlichen Buchungsauftrags bzw. Ihrer
						Buchung an
						<a :href="`mailto:${shoppingCart.Customer.Email}`"> {{ shoppingCart.Customer.Email }} </a>
					</p>
					<p>
						Sollten Sie diese E-Mail nicht erhalten, kontaktieren Sie bitte umgehend unser <b>Service Center</b> unter
						<a :href="`${phoneLink}`">{{ phoneNumber }}</a> Mo-Fr 8-22 Uhr, Sa 9-22 Uhr, So &amp; Feiertage 11-22 Uhr.
					</p>
				</div>
			</div>
		</section>
		<div class="checkout-thank-you__summary">
			<CheckoutInfos
				:iff-code="hotel?.IffCode"
				:hotel-address="hotel?.Address"
				:hotel-category="hotel?.Category"
				:hotel-name="hotel?.Name"
				:tour-operator="shoppingCart.Travel.TourOperator"
				:client="client"
			/>

			<section class="checkout-thank-you__section checkout-thank-you__hotel-infos-section">
				<div class="checkout-thank-you__hotel-infos">
					<div class="checkout-thank-you__hotel-infos-left-column">
						<FeatureList
							:is-long-date="!isPackage"
							:is-checkout-thank-you="true"
							:offer="shoppingCart"
							:location="'thankyouPage'"
							:is-first-feature-bold="true"
						/>
					</div>
				</div>
			</section>
			<section
				v-if="shoppingCart.Travel.Flight"
				class="checkout-thank-you__section checkout-thank-you__checkout-travel-details-section"
			>
				<div class="checkout-thank-you__checkout-travel-details">
					<CheckoutInfoFlightDetails
						v-if="isPackage"
						:outbound="shoppingCart.Travel.Flight.OutboundFlightSegments"
						:inbound="shoppingCart.Travel.Flight.InboundFlightSegments"
					/>
				</div>
			</section>
			<PriceSummary
				class="checkout-thank-you__price-detail-section"
				:storno="storno"
				:actual-traveller-prices="travelerPrices"
				:is-chf="client === 'ch'"
				:final-price-with-currency="finalPriceLabel"
				:total-formatted="finalPriceLabelConverted ? finalPriceLabelConverted : finalPriceLabel"
				:flex-price="stornoPriceLabel"
				:travel-price-with-discount-formatted="travelPriceLabel"
				:travel-price-formatted="finalPriceLabelConverted ? finalPriceLabelConverted : finalPriceLabel"
				:insurance-price="insurance?.priceInformation ?? ''"
				:insurance="insurance"
				:is-package="isPackage"
				:with-overview-header="true"
			/>
			<section
				v-if="printPage"
				class="checkout-thank-you__printer-section"
			>
				<div
					class="checkout-thank-you__icon-group"
					onclick="window.print(); return false;"
				>
					<BaseIcon
						class="checkout-thank-you__print-icon"
						name="printer"
					/>
					<span>Drucken</span>
				</div>
			</section>
		</div>
	</div>
</template>

<script lang="ts" setup>
import BaseIcon from '@lmt-rpb/BaseIcon/BaseIcon.vue';
import type { ClientType } from '@/interfaces/common';
import type { ShoppingCart } from '@/interfaces/checkout/shopping-cart';
import FeatureList from '@lmt-rpb/FeatureList/FeatureList.vue';
import type { FormattedTravelerPrice, LabeledPrice, Price } from '@/interfaces/checkout/offer-data';
import { useStore } from '@/components/common/store';
import { computed } from 'vue';
import { formatPrice } from '@/js/utils/priceUtils';
import CheckoutInfos from '@lmt-rpb/CheckoutInfos/CheckoutInfos.vue';
import CheckoutInfoFlightDetails from '@lmt-rpb/CheckoutInfoFlightDetails/CheckoutInfoFlightDetails.vue';
import PriceSummary from '@lmt-rpb/PriceSummary/PriceSummary.vue';

const store = useStore();

interface Props {
	shoppingCart: ShoppingCart;
	client?: ClientType;
}

const props = withDefaults(defineProps<Props>(), {
	client: 'at',
});
const hotel = props.shoppingCart?.Travel?.Hotel;

const status = computed((): string => props.shoppingCart.Status);

const isPackage = computed((): boolean => props.shoppingCart?.Travel.TravelType === 'Package');

const finalPriceLabelConverted = computed((): string => {
	let pricelabelConverted = '';
	if (props.shoppingCart.Pricing?.Total.Converted?.Currency && props.shoppingCart.Pricing.Total.Converted?.Amount) {
		pricelabelConverted = formatPrice(
			props.shoppingCart.Pricing.Total.Converted.Amount + insurancePrice.value + getStornoPrice(),
			props.shoppingCart.Pricing?.Total.Converted.Currency,
			true
		);
	}
	return pricelabelConverted;
});
const travelPriceLabel = computed((): string => getFormattedPrice(props.shoppingCart.Pricing.Total));

const insurance = computed(() => {
	const insurances = props.shoppingCart.Pricing?.Insurance;
	if (!insurances || !insurances.length) {
		return undefined;
	}
	return {
		insuranceName: insurances[0].Label,
		priceInformation: getFormattedPrice(insurances[0].Price),
	};
});

const titleWording = computed((): string =>
	props.client === 'ch' ? 'Wir wünschen Ihnen schöne Ferien!' : 'Wir wünschen Ihnen einen fantastischen Urlaub!'
);

const insurancePrice = computed((): number => {
	if (!props.shoppingCart.Pricing || !props.shoppingCart.Pricing.Insurance || !props.shoppingCart.Pricing.Insurance.length) {
		return 0;
	}

	return props.shoppingCart.Pricing.Insurance[0].Price.Amount || 0;
});

function getFormattedPrice(price: Price, isConverted = true): string {
	if (isConverted && price.Converted?.Currency && price.Converted?.Amount) {
		return formatPrice(price.Converted.Amount, price.Converted.Currency, true);
	}
	return formatPrice(price.Amount, price.Currency, true);
}

function getStornoPrice(): number {
	if (
		!props.shoppingCart.Pricing ||
		!props.shoppingCart.Pricing.OptionalFlexRate ||
		!props.shoppingCart.Pricing.OptionalFlexRate.Amount
	) {
		return 0;
	}

	let stornoPriceValue =
		props.shoppingCart.Pricing.OptionalFlexRate.Converted?.Amount || props.shoppingCart.Pricing.OptionalFlexRate.Amount;

	if (props.client !== 'ch') {
		stornoPriceValue = props.shoppingCart.Pricing.OptionalFlexRate.Amount;
	}
	return stornoPriceValue;
}

const storno = computed(() => props.shoppingCart.Pricing?.OptionalFlexRate);

const stornoPriceLabel = computed((): string => {
	const currency =
		props.shoppingCart.Pricing?.OptionalFlexRate?.Converted?.Currency || props.shoppingCart.Pricing?.OptionalFlexRate?.Currency;
	if (props.shoppingCart.Pricing?.OptionalFlexRate?.Currency && getStornoPrice()) {
		return formatPrice(getStornoPrice(), currency, true);
	}
	return '';
});

// TODO: Add logic for unverified price for checkUnable offers.
// Check when thank you page is implemented
const finalPriceLabel = computed((): string => {
	let priceLabel = '';
	if (props.shoppingCart.Pricing?.Total.Currency && props.shoppingCart.Pricing.Total.Amount) {
		priceLabel = formatPrice(
			props.shoppingCart.Pricing.Total.Amount + insurancePrice.value + getStornoPrice(),
			props.shoppingCart.Pricing?.Total.Currency,
			true
		);
	}
	return priceLabel;
});

const travelerPrices = computed((): FormattedTravelerPrice[] => {
	if (!props.shoppingCart.Pricing.Travel) {
		return [];
	}

	if (status.value === 'CheckUnable' || status.value === 'Undefined') {
		return [
			{
				Label: 'Alle Personen',
				Price: getFormattedPrice(props.shoppingCart.Pricing.Total),
			},
		];
	}

	return props.shoppingCart.Pricing.Travel?.map((traveler: LabeledPrice) => ({
		Label: traveler.Label,
		Price: getFormattedPrice(traveler.Price),
	}));
});

const phoneNumber = computed((): string => store.state.config.phone);

const phoneLink = computed((): string => `tel:${store.state.config.phoneLink}`);

const printPage = computed((): boolean => typeof window.print === 'function');
</script>

<style lang="scss">
/* stylelint-disable-next-line selector-id-pattern */
.sb-show-main.sb-main-padded,
#rpb_main-content {
	padding: 0 0.5rem;
}
</style>

<style lang="scss" scoped>
$thank-you-page-width: 67.5rem;

:deep(.offer-feature) {
	width: auto !important;
}

:deep(.checkout-info-button.checkout-info-flight-details__button) {
	margin-top: 0.6rem;
	margin-left: -0.2rem;
}

:deep(.offer-feature__shield) {
	height: 1.8rem;
	width: 1.6rem;
	display: block !important;
	margin-right: 0.5rem;
}

:deep(.price-summary:not(.price-summary__sum)) {
	color: $color-text;
}

:deep(.checkout-info-flight-details) {
	flex: 1;
}

:deep(.info-tooltip__icon) {
	margin-left: 0.5rem;
	height: 1.7rem;
	width: 1.7rem;
	vertical-align: middle;
}

:deep(.info-tooltip__icon-container) {
	top: 0 !important;
}

:deep(.feature-list) {
	.feature-list__duration {
		font-size: $font-medium-2;
		font-weight: $font-weight-semibold;
		margin-bottom: 1rem;
	}

	.info-tooltip__icon {
		width: 1.2rem;
		height: 1.2rem;
	}

	.offer-feature {
		color: #333;
	}
}

:deep(.info-tooltip__tooltip) {
	max-width: 67.5rem;
}

.checkout-thank-you {
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: 2.4rem;

	@media (min-width: $breakpoint-small) {
		gap: 2.4rem;
	}

	&__upper {
		display: flex;
		flex-direction: column;
		align-items: center;
		max-width: $thank-you-page-width;

		@media (min-width: $breakpoint-extralarge) {
			width: $thank-you-page-width;
		}
	}

	&__header {
		display: inline-flex;
		align-items: center;
		justify-content: space-around;
		margin-bottom: 2.6rem;
		padding: 0 5rem;

		@media (min-width: $breakpoint-small) {
			margin-bottom: 4.6rem;
		}
	}

	&__svg {
		height: 7rem;
		width: 7rem;
	}

	&__booking-no {
		background-color: $color-lime-light;
		font-size: $font-medium-3;
		font-weight: $font-weight-semibold;
		font-family: $font-family;
		min-height: 4.8rem;
		padding: 1.3rem 1.6rem;
		display: flex;
		align-items: center;
		margin-bottom: 0;
		border-top-right-radius: $border-radius-large;
		border-top-left-radius: $border-radius-large;
		border-right: $color-lime-light 0.2rem solid;
		border-left: $color-lime-light 0.2rem solid;
	}

	&__text {
		&-content {
			background-color: $color-checkout-wrapper;
			padding: 1.7rem;
			border-right: $color-lime-light 0.2rem solid;
			border-left: $color-lime-light 0.2rem solid;
			border-bottom: $color-lime-light 0.2rem solid;
			border-bottom-right-radius: $border-radius-large;
			border-bottom-left-radius: $border-radius-large;
			font-size: $font-small-1;
		}
	}

	&__message {
		font-size: 2.3rem;
		font-weight: $font-weight-semibold;
		margin: 0;
		padding: 0;
		text-align: left;
	}

	&__summary {
		background: $color-checkout-wrapper;
		max-width: $thank-you-page-width;
		margin: 0 auto 2.2rem;
		border-radius: $border-radius-large;
		font-size: $font-small-1;
		font-weight: $font-weight-regular;
		color: $color-text;
		border: 0.2rem solid $color-lime-light;

		@media (min-width: $breakpoint-extralarge) {
			width: $thank-you-page-width;
		}
	}

	&__price-detail-section,
	&__section {
		margin: 0 2.4rem 1.6rem;
	}

	&__section {
		border-bottom: 0.1rem solid $color-primary-l4;
	}

	&__top-label-check {
		width: 1.7rem;
		margin-left: 0.5rem;
		fill: $color-extra;
	}

	&__section-title {
		font-size: $font-medium-2;
		font-weight: $font-weight-semibold;
		color: $color-text;
		margin-bottom: 1rem;
	}

	&__checkout-travel-details {
		margin-bottom: 1.6rem;
	}

	&__checkout-travel-details,
	&__hotel-infos {
		display: flex;
		gap: 0.8rem;
		justify-content: space-between;
		overflow-wrap: anywhere;
	}

	&__chevron-container {
		grid-column-start: 1;
		grid-row-start: 1;
		justify-self: right;
		margin-right: 1.5rem;
	}

	&__room,
	&__traveller-amount {
		font-weight: $font-weight-semibold;
		color: $color-text;
	}

	&__check,
	&__cancel {
		fill: $color-primary;
		width: 1.2rem;
	}

	&__hotel-infos-list {
		list-style: none;
		padding-left: 0;
		margin-bottom: 0;
		line-height: 1.6;

		li {
			display: flex;

			div {
				width: 1.9rem;
				flex-shrink: 0;
			}
		}
	}

	&__hotel-infos-right-column {
		min-width: 11rem;
	}

	&__hotel-infos-section {
		margin-bottom: 1.6rem;
		padding: 0 0 1.6rem;
	}

	@media screen and (min-width: $breakpoint-verysmall) {
		&__section,
		&__price-detail-section {
			margin: 0 4rem 1.6rem;
			padding: 0 0 1.6rem;
		}

		&__checkout-travel-details-section {
			padding: 0;
		}

		&__checkout-travel-details,
		&__hotel-infos {
			padding: 0;
		}
	}

	&__printer-section {
		padding: 1.6rem 2.7rem 1.1rem;
		border-top: 0.1rem solid $color-lime-light;
		display: flex;
		flex-direction: column;
		justify-content: center;
		align-items: flex-end;
	}

	&__icon-group {
		display: flex;
		flex-direction: column;
		align-items: center;
		color: $color-primary;
		font-weight: normal;
		font-size: $font-small-2;
		gap: 0.5rem;
		cursor: pointer;
	}

	&__print-icon {
		fill: $color-primary;
	}

	&__text-content a {
		font-weight: $font-weight-semibold;
	}

	@media print {
		&__print-icon,
		&__printer-section,
		&__header,
		.checkout-summary-button {
			display: none;
		}
	}
}
</style>
